"use client";

import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import withRouter from "../../../components/withRouter";
import {
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  Wand2,
  KeyRound,
  ArrowLeft,
} from "lucide-react";

import * as Yup from "yup";
import { useFormik } from "formik";
import { authClient } from "../../../lib/auth-client";
import { toast } from "react-toastify";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { FieldDescription } from "../../../components/ui/field";
import { Input } from "../../../components/ui/input";

type LoginMethod = "password" | "magicLink" | "emailOTP" | "twoFactor";

const Login = (props: any) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/campaign";

  const [loginMethod, setLoginMethod] = useState<LoginMethod>("password");
  const [passwordShow, setPasswordShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Email OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");

  // Magic Link state
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // 2FA state
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorError, setTwoFactorError] = useState("");

  const validation: any = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberMe: rememberMe,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("E-mail inválido")
        .required("Por favor, insira seu e-mail"),
      password: Yup.string().required("Por favor, digite sua senha"),
      rememberMe: Yup.boolean(),
    }),
    onSubmit: async (values: any) => {
      setLoader(true);
      try {
        const { data, error } = await authClient.signIn.email({
          email: values.email,
          password: values.password,
          rememberMe: values.rememberMe,
        });

        if (error) {
          toast.error(error.message || "Credenciais inválidas");
        } else if (data && (data as any).twoFactorRedirect) {
          setLoginMethod("twoFactor");
        } else {
          navigate(redirectTo);
        }
      } catch {
        // errors handled above
      } finally {
        setLoader(false);
      }
    },
  });

  // Magic Link form validation
  const magicLinkValidation = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("E-mail inválido")
        .required("Por favor, insira seu e-mail"),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      try {
        const { error } = await authClient.signIn.magicLink({
          email: values.email,
          callbackURL: redirectTo,
        });
        if (error) {
          toast.error(error.message || "Erro ao enviar link mágico");
        } else {
          setMagicLinkSent(true);
        }
      } catch {
        toast.error("Erro ao enviar link mágico");
      } finally {
        setLoader(false);
      }
    },
  });

  // Email OTP — Step 1: send OTP
  const emailOtpValidation = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("E-mail inválido")
        .required("Por favor, insira seu e-mail"),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setOtpError("");
      try {
        await authClient.emailOtp.sendVerificationOtp({
          email: values.email,
          type: "sign-in",
        });
        setOtpEmail(values.email);
        setOtpSent(true);
      } catch {
        toast.error("Erro ao enviar código");
      } finally {
        setLoader(false);
      }
    },
  });

  // Email OTP — Step 2: verify OTP and sign in
  const handleVerifyEmailOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode.trim()) {
      setOtpError("Por favor, insira o código");
      return;
    }
    setLoader(true);
    setOtpError("");
    try {
      const { error } = await authClient.signIn.emailOtp({
        email: otpEmail,
        otp: otpCode.trim(),
      });
      if (error) {
        setOtpError(error.message || "Código inválido ou expirado");
      } else {
        navigate(redirectTo);
      }
    } catch {
      setOtpError("Erro ao verificar código");
    } finally {
      setLoader(false);
    }
  };

  // 2FA verification
  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorCode.trim()) {
      setTwoFactorError("Por favor, insira o código");
      return;
    }
    setLoader(true);
    setTwoFactorError("");
    try {
      const { error } = await authClient.twoFactor.verifyOtp({
        code: twoFactorCode.trim(),
      });
      if (error) {
        setTwoFactorError(error.message || "Código inválido");
      } else {
        navigate(redirectTo);
      }
    } catch {
      setTwoFactorError("Erro ao verificar código");
    } finally {
      setLoader(false);
    }
  };

  const handleSocialLogin = async (provider: "google" | "microsoft") => {
    await authClient.signIn.social({
      provider,
      callbackURL: redirectTo,
    });
  };

  const switchMethod = (method: LoginMethod) => {
    setLoginMethod(method);
    setLoader(false);
    setSubmitted(false);
    setOtpSent(false);
    setOtpCode("");
    setOtpError("");
    setMagicLinkSent(false);
    setTwoFactorCode("");
    setTwoFactorError("");
  };

  document.title = "Área Restrita | Ad Pulse";

  // 2FA Verification Screen
  if (loginMethod === "twoFactor") {
    return (
      <React.Fragment>
        <div className="relative isolate min-h-screen bg-white text-gray-900">
          <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
            <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
              <CardHeader className="space-y-1 pb-4 pt-7 text-center">
                <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                  Verificação em duas etapas
                </CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  Insira o código do seu aplicativo autenticador
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
                <form onSubmit={handleVerify2FA} className="space-y-5">
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-gray-700"
                      htmlFor="twoFactorCode"
                    >
                      Código de verificação
                    </label>
                    <Input
                      id="twoFactorCode"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="000000"
                      maxLength={6}
                      value={twoFactorCode}
                      onChange={(e) => {
                        setTwoFactorCode(e.target.value.replace(/\D/g, ""));
                        setTwoFactorError("");
                      }}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 text-center text-lg tracking-[0.5em] text-gray-700 placeholder:text-gray-400 placeholder:tracking-[0.5em] ${
                        twoFactorError
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                    {twoFactorError ? (
                      <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                        <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                          !
                        </span>
                        {twoFactorError}
                      </FieldDescription>
                    ) : null}
                  </div>
                  <Button
                    type="submit"
                    disabled={loader}
                    className="h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                  >
                    {loader ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Verificar
                  </Button>
                </form>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => switchMethod("password")}
                    className="inline-flex items-center gap-1.5 border-0 bg-transparent text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Voltar para o login
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
          <footer className="pb-4 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
            <span className="text-red-500">&#10084;</span>
          </footer>
        </div>
      </React.Fragment>
    );
  }

  // Magic Link Screen
  if (loginMethod === "magicLink") {
    return (
      <React.Fragment>
        <div className="relative isolate min-h-screen bg-white text-gray-900">
          <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
            <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
              <CardHeader className="space-y-1 pb-4 pt-7 text-center">
                <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                  Link Mágico
                </CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  {magicLinkSent
                    ? "Verifique seu e-mail"
                    : "Receba um link de acesso no seu e-mail"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
                {magicLinkSent ? (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-800">
                      Um link de acesso foi enviado para seu e-mail. Clique
                      no link para entrar automaticamente.
                    </div>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => switchMethod("password")}
                        className="inline-flex items-center gap-1.5 border-0 bg-transparent text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Voltar para o login
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        magicLinkValidation.handleSubmit();
                      }}
                      className="space-y-5"
                    >
                      <div>
                        <label
                          className="mb-1.5 block text-sm font-medium text-gray-700"
                          htmlFor="magicLinkEmail"
                        >
                          E-mail
                        </label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <Input
                            id="magicLinkEmail"
                            name="email"
                            type="email"
                            placeholder="seu@email.com"
                            autoComplete="email"
                            onChange={magicLinkValidation.handleChange}
                            onBlur={magicLinkValidation.handleBlur}
                            value={magicLinkValidation.values.email}
                            className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pl-10 text-sm text-gray-700 placeholder:text-gray-400 ${
                              magicLinkValidation.touched.email &&
                              magicLinkValidation.errors.email
                                ? "!border-red-500 focus-visible:!ring-red-500"
                                : "focus-visible:ring-red-500/20"
                            }`}
                          />
                        </div>
                        {magicLinkValidation.touched.email &&
                        magicLinkValidation.errors.email ? (
                          <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                            <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                              !
                            </span>
                            {magicLinkValidation.errors.email}
                          </FieldDescription>
                        ) : null}
                      </div>
                      <Button
                        type="submit"
                        disabled={loader}
                        className="h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                      >
                        {loader ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Enviar link de acesso
                      </Button>
                    </form>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => switchMethod("password")}
                        className="inline-flex items-center gap-1.5 border-0 bg-transparent text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Voltar para o login com senha
                      </button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
          <footer className="pb-4 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
            <span className="text-red-500">&#10084;</span>
          </footer>
        </div>
      </React.Fragment>
    );
  }

  // Email OTP Screen
  if (loginMethod === "emailOTP") {
    return (
      <React.Fragment>
        <div className="relative isolate min-h-screen bg-white text-gray-900">
          <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
            <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
              <CardHeader className="space-y-1 pb-4 pt-7 text-center">
                <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                  Código por E-mail
                </CardTitle>
                <CardDescription className="text-sm text-gray-500">
                  {otpSent
                    ? `Insira o código enviado para ${otpEmail}`
                    : "Receba um código de acesso no seu e-mail"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
                {!otpSent ? (
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        emailOtpValidation.handleSubmit();
                      }}
                      className="space-y-5"
                    >
                      <div>
                        <label
                          className="mb-1.5 block text-sm font-medium text-gray-700"
                          htmlFor="otpEmail"
                        >
                          E-mail
                        </label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <Input
                            id="otpEmail"
                            name="email"
                            type="email"
                            placeholder="seu@email.com"
                            autoComplete="email"
                            onChange={emailOtpValidation.handleChange}
                            onBlur={emailOtpValidation.handleBlur}
                            value={emailOtpValidation.values.email}
                            className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pl-10 text-sm text-gray-700 placeholder:text-gray-400 ${
                              emailOtpValidation.touched.email &&
                              emailOtpValidation.errors.email
                                ? "!border-red-500 focus-visible:!ring-red-500"
                                : "focus-visible:ring-red-500/20"
                            }`}
                          />
                        </div>
                        {emailOtpValidation.touched.email &&
                        emailOtpValidation.errors.email ? (
                          <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                            <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                              !
                            </span>
                            {emailOtpValidation.errors.email}
                          </FieldDescription>
                        ) : null}
                      </div>
                      <Button
                        type="submit"
                        disabled={loader}
                        className="h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                      >
                        {loader ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Enviar código
                      </Button>
                    </form>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => switchMethod("password")}
                        className="inline-flex items-center gap-1.5 border-0 bg-transparent text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Voltar para o login com senha
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <form onSubmit={handleVerifyEmailOtp} className="space-y-5">
                      <div>
                        <label
                          className="mb-1.5 block text-sm font-medium text-gray-700"
                          htmlFor="otpCode"
                        >
                          Código de verificação
                        </label>
                        <Input
                          id="otpCode"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          placeholder="000000"
                          maxLength={6}
                          value={otpCode}
                          onChange={(e) => {
                            setOtpCode(e.target.value.replace(/\D/g, ""));
                            setOtpError("");
                          }}
                          className={`h-11 rounded-lg border-slate-200 bg-slate-50 text-center text-lg tracking-[0.5em] text-gray-700 placeholder:text-gray-400 placeholder:tracking-[0.5em] ${
                            otpError
                              ? "!border-red-500 focus-visible:!ring-red-500"
                              : "focus-visible:ring-red-500/20"
                          }`}
                        />
                        {otpError ? (
                          <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                            <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                              !
                            </span>
                            {otpError}
                          </FieldDescription>
                        ) : null}
                      </div>
                      <Button
                        type="submit"
                        disabled={loader}
                        className="h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                      >
                        {loader ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : null}
                        Entrar
                      </Button>
                    </form>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => {
                          setOtpSent(false);
                          setOtpCode("");
                          setOtpError("");
                        }}
                        className="border-0 bg-transparent text-sm font-medium text-gray-500 hover:text-gray-700"
                      >
                        Reenviar código
                      </button>
                      <button
                        type="button"
                        onClick={() => switchMethod("password")}
                        className="inline-flex items-center gap-1.5 border-0 bg-transparent text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Voltar
                      </button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
          <footer className="pb-4 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
            <span className="text-red-500">&#10084;</span>
          </footer>
        </div>
      </React.Fragment>
    );
  }

  // Default: Password Login Screen
  return (
    <React.Fragment>
      <div className="relative isolate min-h-screen bg-white text-gray-900">
        <div className="flex min-h-[calc(100vh-88px)] items-center justify-center px-4 pb-10">
          <Card className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-sm">
            <CardHeader className="space-y-1 pb-4 pt-7 text-center">
              <CardTitle className="text-xl font-bold text-gray-900 sm:text-3xl">
                Bem-vindo ao AdPulse
              </CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Entre na sua conta para continuar
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5 px-4 pb-5 sm:px-7 sm:pb-7">
              <form
                autoComplete="on"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSubmitted(true);
                  validation.handleSubmit();
                }}
                className="space-y-5"
              >
                {/* Email */}
                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                    htmlFor="email"
                  >
                    E-mail
                  </label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="seu@email.com"
                      autoComplete="email"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pl-10 text-sm text-gray-700 placeholder:text-gray-400 ${
                        submitted && validation.errors.email
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                  </div>
                  {submitted && validation.errors.email ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                        !
                      </span>
                      {validation.errors.email}
                    </FieldDescription>
                  ) : null}
                </div>

                {/* Password */}
                <div>
                  <div className="mb-1.5 flex items-center">
                    <label
                      className="text-sm font-medium text-gray-700"
                      htmlFor="password"
                    >
                      Senha
                    </label>
                  </div>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={passwordShow ? "text" : "password"}
                      autoComplete="current-password"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password}
                      className={`h-11 rounded-lg border-slate-200 bg-slate-50 !pl-10 !pr-11 text-sm text-gray-700 placeholder:text-gray-400 ${
                        submitted && validation.errors.password
                          ? "!border-red-500 focus-visible:!ring-red-500"
                          : "focus-visible:ring-red-500/20"
                      }`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setPasswordShow(!passwordShow)}
                      className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 border-0 bg-transparent !px-0 !py-0 text-gray-400 shadow-none hover:bg-transparent hover:text-gray-600 focus-visible:outline-none focus-visible:ring-0"
                      aria-label={
                        passwordShow ? "Ocultar senha" : "Mostrar senha"
                      }
                      tabIndex={-1}
                    >
                      {passwordShow ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {submitted && validation.errors.password ? (
                    <FieldDescription className="mt-1 flex items-center gap-1 !text-red-600">
                      <span className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white">
                        !
                      </span>
                      {validation.errors.password}
                    </FieldDescription>
                  ) : null}
                </div>

                {/* Remember me + Forgot password */}
                <div className="mt-3 flex items-center justify-between">
                  <label
                    htmlFor="rememberMe"
                    className="flex cursor-pointer items-center gap-2 my-0"
                  >
                    <button
                      id="rememberMe"
                      type="button"
                      role="switch"
                      aria-checked={rememberMe}
                      onClick={() => {
                        const nextValue = !rememberMe;
                        setRememberMe(nextValue);
                        validation.setFieldValue("rememberMe", nextValue);
                      }}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-0 outline-none !p-0 transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-red-500/20 ${
                        rememberMe ? "bg-red-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`pointer-events-none absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all duration-200 ease-out ${
                          rememberMe ? "left-[17px]" : "left-0.5"
                        }`}
                      />
                    </button>
                    <span className="text-sm leading-none text-gray-600">
                      Lembrar-me
                    </span>
                  </label>

                  <Link
                    to="/forgot-password"
                    className="text-sm leading-none font-medium text-red-600 transition-colors hover:text-red-700"
                  >
                    Esqueci minha senha
                  </Link>
                </div>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  disabled={loader}
                  className="mt-3 h-11 w-full rounded-lg border-0 bg-red-600 font-semibold text-white hover:bg-red-700 active:bg-red-800"
                >
                  {loader ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Entrar
                </Button>
              </form>

              {/* Social Divider */}
              <div className="mt-3 mb-2 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  ou continue com
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              {/* Social Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {/* Google */}
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 gap-2.5 rounded-lg border-gray-200 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100"
                  onClick={() => handleSocialLogin("google")}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z"
                      fill="#34A853"
                    />
                    <path
                      d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Google
                  </span>
                </Button>

                {/* Microsoft */}
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 gap-2.5 rounded-lg border-gray-200 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100"
                  onClick={() => handleSocialLogin("microsoft")}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="8.5"
                      height="8.5"
                      fill="#F25022"
                    />
                    <rect
                      x="9.5"
                      y="0"
                      width="8.5"
                      height="8.5"
                      fill="#7FBA00"
                    />
                    <rect
                      x="0"
                      y="9.5"
                      width="8.5"
                      height="8.5"
                      fill="#00A4EF"
                    />
                    <rect
                      x="9.5"
                      y="9.5"
                      width="8.5"
                      height="8.5"
                      fill="#FFB900"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Microsoft
                  </span>
                </Button>
              </div>

              {/* Passwordless Divider */}
              <div className="mt-1 mb-2 flex items-center gap-3">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                  ou entre sem senha
                </span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>

              {/* Passwordless Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 gap-2.5 rounded-lg border-gray-200 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100"
                  onClick={() => switchMethod("magicLink")}
                >
                  <Wand2 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Link Mágico
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="h-11 gap-2.5 rounded-lg border-gray-200 px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100"
                  onClick={() => switchMethod("emailOTP")}
                >
                  <KeyRound className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Código por E-mail
                  </span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <footer className="pb-4 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Ad Pulse. Feito com{" "}
          <span className="text-red-500">&#10084;</span>
        </footer>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
